export const mockPortfolioData = {
  personal: {
    name: "Darrly Webb",
    title: "Senior Software Engineer",
    subtitle: "AI & Systems Engineer",
    tagline:
      "Senior Software Engineer at thoughtbot, specializing in AI & systems. Core contributor to PolyAI Agent Studio — the world's leading voice-first conversational AI platform. Expert in LLM observability, real-time interfaces, and enterprise voice agent architecture.",
    email: "ddarrlywebb@gmail.com",
    phone: "+1 206 489 0626",
    location: "Pasco, WA 99301",
    resumeUrl: "https://drive.google.com/file/d/1JzwXUaMj0GasWAvYl-kibWh2VNZokUBT/view?usp=sharing",
    linkedin: "#",
    portfolio: "https://super-dw-dev.vercel.app/",
    github: "https://github.com/dwdontwait"
  },

  about: {
    summary:
      "I'm a software engineer who enjoys building systems step-by-step—starting with clean fundamentals and then iterating quickly once the foundation is solid. Most days I'm working across TypeScript, Python, and AI tooling to turn complex workflows into smooth, intuitive, reliable product experiences. Professionally, I specialize in real-time interfaces, LLM-integrated systems, and full-stack architecture that balances performance with maintainability. I thrive in small, hands-on teams where ownership is shared, experimentation is encouraged, and every engineer has a direct impact on how the product works and feels.",
    personal:
      "I'm a software engineer who enjoys the process of building things step-by-step, first focusing on the fundamentals, then speeding up once everything falls into place. Most days, I'm deep into TypeScript, Python, and AI tools, working on turning complex systems into something smooth, intuitive, and reliable. Away from work, I find peace in simple routines — like savoring a quiet morning coffee, taking long walks alone, and spending time with the people who matter most. I keep my mind balanced with fitness, books, and the occasional reset on weekends. I thrive in early-stage teams where everyone takes ownership, experimentation is embraced, and everyone has a hand in shaping the product from the ground up.",
    experience:
      "10+ years • Google → Databricks → H2O → thoughtbot • Focus on AI/ML integration and real-time systems",
    expertise:
      "React/Next.js • Node.js/Python/Go • LLM integration • Real-time systems • Agent Studio development • Observability",
    mindset:
      "I thrive in small, hands-on teams where ownership is shared, experimentation is encouraged, and every engineer has a direct impact on how the product works and feels.",
    metrics: [
      "Built Agent Studio enabling enterprise voice agent design",
      "Delivered observability surfaces reducing triage time", 
      "Integrated RAG pipelines improving routing precision",
      "Tuned WebSocket + gRPC streaming for conversational fluidity",
      "Led code reviews and architectural discussions as senior IC"
    ]
  },

  experience: [
    {
      id: "1",
      company: "thoughtbot",
      position: "Senior Software Engineer — Consultant (AI & Product Delivery)",
      duration: "12/2020 - Present",
      description:
        "🎯 Primary Focus: PolyAI Agent Studio — World's leading voice-first conversational AI platform",
      achievements: [
        "Delivered core features for Agent Studio using React, Next.js, TypeScript, React Flow, and TailwindCSS—enabling enterprise teams to design, test, and deploy sophisticated voice agents.",
        "Built conversation-flow editors, transcript explorers, and trace inspectors with observability powered by Langfuse, LangChain, LangGraph, and LiteLLM, improving debugging speed for agent misclassifications.",
        "Developed Node.js/Python microservices for retrieval logic, semantic routing, and multi-turn state management, increasing reliability for large-scale call flows.",
        "Designed LLM observability surfaces exposing latency, grounding decisions, token usage, and fallback behavior—reducing triage time and improving insight into model reasoning.",
        "Integrated RAG pipelines and domain-specific prompting patterns to improve routing precision and intent clarity for high-impact customer deployments.",
        "Tuned WebSocket + gRPC streaming to reduce round-trip latency, improving conversational fluidity and natural interruption behavior.",
        "Contributed heavily to code reviews, architectural discussions, and UX iteration cycles as a senior IC embedded in PolyAI's product and research teams."
      ],
      additionalWork: {
        title: "Additional Consulting Work (Ad-Hoc / Part-Time)",
        items: [
          "Built Storybook-driven microfrontends and reusable component libraries for multi-product client teams.",
          "Delivered full-stack features for early-stage startups using React, Next.js, Node.js, NestJS, and FastAPI.",
          "Established CI/CD pipelines, preview environments, and Playwright/Jest test patterns to tighten release cycles.",
          "Leveraged AI-assisted development tools (Cursor, Windsurf, Claude Code) to accelerate refactors and improve iteration speed."
        ]
      },
      order: 1,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "2",
      company: "H2O",
      position: "Senior Software Engineer",
      duration: "10/2018 - 11/2020",
      description:
        "Built full-stack features for AutoML and NLP tools with enterprise focus",
      achievements: [
        "Built full-stack features for AutoML and NLP tools using React, TypeScript, D3, Plotly.js, and internal design systems—improving model insight visibility for enterprise users.",
        "Developed Python/FastAPI services orchestrating model training, scoring pipelines, and experiment metadata, integrating deeply with AutoML and internal NLP workflows.",
        "Delivered real-time training progress and diagnostics via WebSocket streaming, enhancing transparency and reducing abandoned runs.",
        "Created interactive SHAP and time-series visualizations optimized with virtualization for large datasets.",
        "Improved platform reliability through structured logging, distributed tracing, and monitoring with Datadog and Sentry, reducing issue investigation time.",
        "Collaborated with ML research teams to productionize prototype notebooks into stable product features.",
        "Mentored junior engineers and contributed to system-design decisions as a senior IC."
      ],
      order: 2,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "3",
      company: "Databricks",
      position: "Software Engineer II",
      duration: "01/2017 - 09/2018",
      description:
        "Built React + TypeScript UIs for cluster monitoring and Delta Lake operations",
      achievements: [
        "Built React + TypeScript UIs for cluster monitoring, job lifecycle management, and Delta Lake operations used across enterprise data workflows.",
        "Developed Python APIs for Spark ETL, feature engineering, and batch ML jobs, improving performance and reliability of core pipelines.",
        "Implemented hybrid REST + WebSocket channels for real-time job status, log streams, and compute metrics.",
        "Contributed to pre-LLM NLP-powered SQL helpers using schema-aware models, helping users write queries more efficiently.",
        "Refactored shared component libraries to improve consistency and reduce UI duplication across the platform.",
        "Enhanced debugging tools, caching layers, and performance diagnostics for Spark and compute systems.",
        "Took on feature scoping, technical planning, and iterative delivery as a mid-level IC."
      ],
      order: 3,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "4",
      company: "Google",
      position: "Software Engineer",
      duration: "06/2015 - 12/2016",
      description:
        "Shipped AngularJS and early Angular 2+ features using Google's Material Design system",
      achievements: [
        "Shipped AngularJS (v1.x → 1.5) and early Angular 2+ features using Google's Material Design system and Closure-based internal tooling.",
        "Built Go and Python microservices for metrics ingestion, access control, audit logging, and health-check pipelines powering major GCP Console surfaces.",
        "Developed reusable Angular components (tables, dialogs, wizards) adopted across IAM and Monitoring teams.",
        "Collaborated with UX research and PM teams to refine permission workflows and dashboard usability.",
        "Improved UI performance by optimizing digest cycles, template caching, and resource prefetching—reducing load times on high-traffic views."
      ],
      order: 4,
      createdAt: "2025-01-01T00:00:00.000Z"
    }
  ],

  projects: [
    {
      id: "1",
      title: "PolyAI Agent Studio",
      description: "The world's only voice-first omnichannel platform for conversational AI. Built core features enabling enterprise teams to design, test, and deploy sophisticated voice agents with real-time observability.",
      tech: ["React", "Next.js", "TypeScript", "React Flow", "TailwindCSS", "Langfuse", "LangChain", "LangGraph", "LiteLLM", "WebSocket", "gRPC"],
      url: "https://poly.ai/blog/introducing-agent-studio/",
      github: "#",
      featured: true,
      category: "ai",
      industry: "Voice AI",
      company: "thoughtbot/PolyAI",
      logo: "/images/polyai-logo-inverted-rgb-900px-w-72ppi-min.webp",
      consultingLogo: "/images/thoughtbot.png",
      impact: "Delivered conversation-flow editors, transcript explorers, and LLM observability surfaces—reducing debugging time and improving conversational fluidity",
      order: 1,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "2",
      title: "ClinicalNotes.ai",
      description:
        "HIPAA-compliant AI platform for behavioral health documentation. Built mobile app, Chrome extension, and web app for automated compliance auditing and template-based data extraction.",
      tech: [
        "React",
        "React Native", 
        "Chrome Extensions",
        "Node.js",
        "Python",
        "HIPAA Compliance",
        "AI/NLP",
        "EHR Integration"
      ],
      url: "https://www.clinicalnotes.ai/",
      github: "#",
      featured: true,
      category: "ai",
      industry: "Healthcare",
      company: "thoughtbot",
      logo: "/images/clinically.jfif",
      consultingLogo: "/images/thoughtbot.png",
      impact:
        "Reduced documentation burden by 60% while ensuring 100% HIPAA compliance and real-time audit support",
      order: 2,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "3",
      title: "Constrafor Construction Finance",
      description:
        "All-in-one fintech platform for general contractors and subcontractors. Built procurement workflows, invoice tracking, and AI-powered CFO replacement platform for construction projects.",
      tech: [
        "React",
        "Next.js",
        "TypeScript",
        "Node.js",
        "FinTech APIs",
        "Payment Processing",
        "AI/ML",
        "Real-time Analytics"
      ],
      url: "https://www.constrafor.com",
      additionalUrl: "https://www.cru.ai",
      github: "#",
      featured: true,
      category: "ai",
      industry: "Construction",
      company: "thoughtbot",
      logo: "/images/constrafor.png",
      consultingLogo: "/images/thoughtbot.png",
      impact:
        "Processing $500M+ in payments, serving 40,000+ contractors with AI-powered risk management and automated finance workflows",
      order: 3,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "4",
      title: "Fravity.ai",
      description:
        "AI agent platform for fraud detection and anti-money laundering investigations. Built multimodal agents for business risk profiling, KYC, transaction monitoring, and regulatory compliance.",
      tech: [
        "React",
        "TypeScript",
        "Python",
        "AI Agents",
        "Computer Vision",
        "NLP",
        "Fraud Detection",
        "AML Compliance"
      ],
      url: "https://www.fravity.ai/",
      github: "#",
      featured: true,
      category: "ai",
      industry: "Insurance",
      company: "thoughtbot",
      logo: "/images/fravity.png",
      consultingLogo: "/images/thoughtbot.png",
      impact:
        "70+ ready-made agents for adverse media, sanctions, and document analysis—reducing false positives by 80% in financial investigations",
      order: 4,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "5",
      title: "MarketerX AI CMO Platform",
      description: "AI CMO platform for marketing automation. Built MVP working closely with CEO and ops manager, enabling enterprise-level marketing intelligence without complexity or cost.",
      tech: ["React", "Next.js", "TypeScript", "AI/ML", "Marketing APIs", "Analytics"],
      url: "https://www.marketerx.com/why-marketerx",
      github: "#",
      featured: true,
      category: "ai",
      industry: "Marketing",
      company: "thoughtbot",
      logo: "/images/marketerx.png",
      consultingLogo: "/images/thoughtbot.png",
      impact: "Early-stage MVP for revolutionary marketing automation platform with AI-driven strategy and execution",
      order: 5,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "6",
      title: "Cake.ai Infrastructure Platform",
      description: "Modern enterprise AI governance platform for cost control, compliance, and flexibility. Built infrastructure management tools for AI workloads across any environment.",
      tech: ["React", "TypeScript", "Kubernetes", "AI Infrastructure", "Cost Management", "Compliance"],
      url: "https://www.cake.ai",
      github: "#",
      featured: true,
      category: "ai",
      industry: "AI Infrastructure",
      company: "thoughtbot",
      logo: "/images/cake-logo.svg",
      consultingLogo: "/images/thoughtbot.png",
      impact: "Full-stack control for AI models, pipelines, and policies with enterprise governance and cost optimization",
      order: 6,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "7",
      title: "RentSpree Platform",
      description: "Comprehensive tenant screening and rental management platform. Built tools for landlords and renters with AI-powered property matching and automated screening workflows.",
      tech: ["React", "Node.js", "Python", "Real Estate APIs", "Payment Processing", "Background Checks"],
      url: "https://www.rentspree.com/",
      github: "#",
      featured: true,
      category: "domain",
      industry: "PropTech",
      company: "thoughtbot",
      logo: "/images/rentspree.png",
      consultingLogo: "/images/thoughtbot.png",
      impact: "Serving 3M+ users with top-rated tenant screening, reducing lease time and improving rental experiences",
      order: 7,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "8",
      title: "Arini AI Dental Receptionists",
      description: "AI receptionists for dental practices handling appointment scheduling through inbound/outbound calls, SMS, and web chat. Built 24/7 patient communication system.",
      tech: ["React", "Node.js", "Python", "Voice AI", "NLP", "SMS APIs", "Healthcare Integration"],
      url: "https://www.arini.ai/",
      github: "#",
      featured: true,
      category: "ai",
      industry: "Healthcare",
      company: "thoughtbot",
      logo: "/images/arini.jfif",
      consultingLogo: "/images/thoughtbot.png",
      impact: "Increased revenue by 12% and reduced missed calls by 80% for hundreds of dental practices across US and Canada",
      order: 8,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "9",
      title: "H2O Driverless AI",
      description: "Award-winning AutoML platform with automated feature engineering, model development, and explainability. Built interactive UIs for enterprise ML workflows and real-time training diagnostics.",
      tech: ["React", "TypeScript", "D3.js", "Plotly.js", "Python", "FastAPI", "WebSockets", "AutoML"],
      url: "https://h2o.ai/platform/ai-cloud/make/h2o-driverless-ai/",
      github: "#",
      featured: true,
      category: "domain",
      industry: "AutoML",
      company: "H2O.ai",
      logo: "/images/h2o.png",
      impact: "Automated ML model development reducing time-to-production from weeks to hours with enterprise-grade explainability",
      order: 9,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "10",
      title: "H2O GPT Enterprise",
      description: "Enterprise document AI platform with multimodal analysis, RAG capabilities, and citation-based verification. Built secure, private data processing for enterprise knowledge bases.",
      tech: ["React", "TypeScript", "Python", "LLMs", "RAG", "Vector Databases", "Document AI"],
      url: "https://h2o.ai/platform/enterprise-h2ogpte/",
      github: "#",
      featured: true,
      category: "ai",
      industry: "Enterprise AI",
      company: "H2O.ai",
      logo: "/images/h2o.png",
      impact: "Enabled secure enterprise document analysis with multimodal capabilities and transparent citation-based responses",
      order: 10,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "11",
      title: "Databricks Lakehouse Platform",
      description: "Built React + TypeScript UIs for unified analytics platform combining data lakes and warehouses. Developed cluster monitoring, job lifecycle management, and Delta Lake operations.",
      tech: ["React", "TypeScript", "Python", "Apache Spark", "Delta Lake", "WebSockets", "Data Engineering"],
      url: "https://www.databricks.com/product/data-lakehouse",
      github: "#",
      featured: true,
      category: "domain",
      industry: "Data Platform",
      company: "Databricks",
      logo: "/images/databricks.jfif",
      impact: "Unified data and AI workloads on single platform, processing petabytes of data with real-time job monitoring",
      order: 11,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "12",
      title: "Google Cloud Console",
      description: "Shipped AngularJS and early Angular 2+ features using Google's Material Design system. Built reusable components for IAM and Monitoring teams with Go/Python microservices.",
      tech: ["AngularJS", "Angular 2+", "Material Design", "Go", "Python", "Microservices", "GCP"],
      url: "https://console.cloud.google.com",
      github: "#",
      featured: true,
      category: "domain",
      industry: "Cloud Platform",
      company: "Google",
      logo: "/images/google.png",
      impact: "Built reusable Angular components adopted across multiple GCP Console teams, improving UI consistency and performance",
      order: 12,
      createdAt: "2025-01-01T00:00:00.000Z"
    }
  ],

  techStack: {
    frontend: [
      "React",
      "Next.js",
      "TypeScript",
      "Angular",
      "Vue",
      "React Flow",
      "Redux",
      "Zustand",
      "React Query",
      "Storybook",
      "TailwindCSS",
      "D3.js",
      "Plotly.js",
      "Cypress",
      "Playwright"
    ],
    backend: [
      "Node.js",
      "Express",
      "NestJS",
      "Python",
      "FastAPI",
      "Flask",
      "Go",
      "PostgreSQL",
      "Redis",
      "MongoDB",
      "Prisma",
      "gRPC",
      "REST",
      "GraphQL",
      "Kafka"
    ],
    aiml: [
      "Langfuse",
      "LangChain", 
      "LangGraph",
      "LiteLLM",
      "OpenAI APIs",
      "RAG pipelines",
      "embeddings",
      "semantic search",
      "AutoML",
      "SHAP",
      "classical NLP (spaCy, NLTK)",
      "ML observability"
    ],
    cloud: [
      "GCP",
      "AWS", 
      "Docker",
      "Kubernetes",
      "Terraform",
      "GitHub Actions",
      "Cloud Build",
      "Datadog",
      "Sentry",
      "OpenTelemetry"
    ],
    practices: [
      "System design",
      "design systems",
      "rapid prototyping",
      "real-time data apps",
      "code reviews",
      "performance optimization",
      "CI/CD",
      "DevOps",
      "Team Leadership",
      "Mentoring"
    ]
  },

  lookingFor: {
    title: "What I'm Looking For",
    description:
      "I'm looking for opportunities to continue building AI-integrated systems and real-time interfaces in small, hands-on teams where ownership is shared and experimentation is encouraged. Interested in roles where I can make a direct impact on product experience, whether that's architecting LLM observability systems, building conversational AI platforms, or developing full-stack features that turn complex workflows into smooth, intuitive experiences."
  }
};
